import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Check, X, Loader2, ArrowUpRight } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [pkg, setPkg] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setPkg(data.package_id);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.payment_status === "failed" || data.payment_status === "expired") { setStatus("failed"); return; }
      } catch { /* ignore */ }
      if (tries < 20) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="max-w-[900px] mx-auto px-6 py-24" data-testid="payment-success-page">
      <div className="te-terminal-window">
        <div className="border-b border-[#1F2833] px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-emerald-400 te-pulse-dot"></span>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em]">payments --status</span>
          </div>
          <span className="font-mono text-[11px] text-[#FF3355]">{status.toUpperCase()}</span>
        </div>
        <div className="p-10 lg:p-14 text-center">
          {status === "polling" ? (
            <>
              <Loader2 size={40} className="text-[#FF3355] mx-auto animate-spin" />
              <h2 className="mt-6 font-display text-2xl">Confirming payment...</h2>
              <p className="mt-3 text-[#C5C6C7]">Hang tight — Stripe usually confirms in 3–5 seconds.</p>
            </>
          ) : status === "paid" ? (
            <>
              <div className="inline-flex items-center justify-center h-16 w-16 border-2 border-[#FF3355] mb-6">
                <Check size={28} className="text-emerald-400" />
              </div>
              <h2 className="font-display text-3xl">Payment received.</h2>
              <p className="mt-3 text-[#C5C6C7]">Confirmation sent. {pkg && <>Your <span className="text-[#FF3355]">{pkg}</span> engagement is live.</>}</p>
              <Link to="/portal" data-testid="payment-to-portal" className="mt-8 inline-flex items-center gap-2 bg-[#B91C2C] text-[#F5F5F7] px-6 py-3 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355]">Go to portal <ArrowUpRight size={14} /></Link>
            </>
          ) : (
            <>
              <div className="inline-flex items-center justify-center h-16 w-16 border-2 border-[#FF3355]/60 mb-6">
                <X size={28} className="text-[#FF3355]" />
              </div>
              <h2 className="font-display text-3xl">Payment not confirmed.</h2>
              <p className="mt-3 text-[#C5C6C7]">If you were charged, we&apos;ll spot it on our end and reach out.</p>
              <Link to="/services" className="mt-8 inline-flex items-center gap-2 border border-[#1F2833] hover:border-[#FF3355] px-6 py-3 font-mono text-xs uppercase tracking-[0.22em]">Back to services</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-[900px] mx-auto px-6 py-24" data-testid="payment-cancel-page">
      <div className="te-terminal-window">
        <div className="p-10 text-center">
          <h2 className="font-display text-3xl">Checkout cancelled.</h2>
          <p className="mt-3 text-[#C5C6C7]">No charge was made. You can restart anytime.</p>
          <Link to="/services" className="mt-8 inline-flex items-center gap-2 bg-[#B91C2C] text-[#F5F5F7] px-6 py-3 font-mono text-xs uppercase tracking-[0.22em] hover:bg-[#FF3355]">Back to services</Link>
        </div>
      </div>
    </div>
  );
}
